/**
 * The arrow. The product's signature, and the only thing on screen that matters.
 *
 * A rendered 3D arrow floating above a compass rose that counter-rotates so north stays true
 * (`ProductOverview.md` §4.3). Both are smoothed with shortest-path interpolation, so the arrow
 * behaves like a physical needle rather than a nervous cursor — turning 180° swings it smoothly
 * 180° the other way, and it never spins the long way around.
 *
 * Screen-space convention: 0° is straight up the screen, growing clockwise. The scene looks
 * down the +Y axis, so screen-up is -Z, and a clockwise screen angle is a *negative* rotation
 * about Y.
 */

import { ARROW, lerpAngle, normalizeDegrees, smoothAngle } from '@beeline/shared';
import {
  AmbientLight,
  Color,
  DirectionalLight,
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  PointLight,
  RingGeometry,
  Scene,
  Shape,
  TorusGeometry,
  WebGLRenderer,
  type BufferGeometry,
  type Material,
} from 'three';

const DEG2RAD = Math.PI / 180;

/** Screen angle (clockwise from up) to a rotation about the scene's vertical axis. */
const yawFor = (screenDegrees: number): number => -screenDegrees * DEG2RAD;

const AMBER = 0xffb454;
const AMBER_DEEP = 0xff8a3d;
const INK = 0x0e1520;
const HAIRLINE = 0x23303f;

export interface ArrowSceneState {
  /** Where to point, in screen degrees clockwise from up. Null hides the arrow. */
  relative: number | null;
  /** Device heading, so the rose can counter-rotate. Null leaves it north-up. */
  heading: number | null;
  onCourse: boolean;
  /** Desaturates the whole scene — a frozen arrow must look frozen. */
  stale: boolean;
}

export interface ArrowScene {
  canvas: HTMLCanvasElement;
  update: (state: ArrowSceneState) => void;
  /** Jump straight to the target angles, with no swing. Used on the first fix. */
  snap: () => void;
  destroy: () => void;
}

/**
 * The world radius the camera keeps in frame. Everything else is sized against it, so the
 * scene composes identically on a narrow phone and a wide desktop window.
 */
const FRAMED_RADIUS = 2.05;
const ROSE_RADIUS = 1.92;

/** The chevron, drawn in shape space with +Y as the tip. */
function arrowShape(): Shape {
  const shape = new Shape();
  shape.moveTo(0, 1.32);
  shape.lineTo(0.84, -0.96);
  shape.lineTo(0, -0.42);
  shape.lineTo(-0.84, -0.96);
  shape.closePath();
  return shape;
}

/** Four ticks and a ring. North is amber; the rest are quiet. */
function buildRose(disposables: (BufferGeometry | Material)[]): Group {
  const rose = new Group();

  const ringGeometry = new TorusGeometry(ROSE_RADIUS, 0.014, 8, 96);
  const ringMaterial = new MeshStandardMaterial({
    color: HAIRLINE,
    roughness: 0.9,
    metalness: 0,
  });
  const ring = new Mesh(ringGeometry, ringMaterial);
  ring.rotation.x = -Math.PI / 2;
  rose.add(ring);
  disposables.push(ringGeometry, ringMaterial);

  const northMaterial = new MeshStandardMaterial({
    color: AMBER,
    emissive: new Color(AMBER_DEEP),
    emissiveIntensity: 0.45,
    roughness: 0.5,
  });
  const tickMaterial = new MeshStandardMaterial({ color: HAIRLINE, roughness: 0.9 });
  disposables.push(northMaterial, tickMaterial);

  // A wedge per cardinal point, north longer and lit so true north is unmistakable.
  for (const bearing of [0, 90, 180, 270]) {
    const isNorth = bearing === 0;
    const geometry = new RingGeometry(
      ROSE_RADIUS - (isNorth ? 0.3 : 0.14),
      ROSE_RADIUS - 0.02,
      1,
      1,
      // RingGeometry starts at +X and sweeps anticlockwise; centre the wedge on the cardinal
      // direction once the whole rose is laid into the ground plane.
      Math.PI / 2 - (isNorth ? 0.05 : 0.022),
      isNorth ? 0.1 : 0.044,
    );
    const tick = new Mesh(geometry, isNorth ? northMaterial : tickMaterial);
    tick.rotation.x = -Math.PI / 2;
    tick.rotation.z = -bearing * DEG2RAD;
    rose.add(tick);
    disposables.push(geometry);
  }

  return rose;
}

export function createArrowScene(): ArrowScene {
  const canvas = document.createElement('canvas');
  canvas.className = 'arrow-canvas';

  const disposables: (BufferGeometry | Material)[] = [];

  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setClearColor(INK, 0);
  // Battery: a phone's device pixel ratio can be 3 or 4, which triples the fill rate for a
  // difference nobody can see on an arrow (`ProductOverview.md` §6).
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, ARROW.maxPixelRatio));

  const scene = new Scene();

  const FOV = 38;
  const camera = new PerspectiveCamera(FOV, 1, 0.1, 200);
  // Above and slightly behind, so the arrow reads as an object in space rather than a glyph.
  const CAMERA_DIRECTION = { y: 0.94, z: 0.35 };

  scene.add(new AmbientLight(0xffffff, 0.55));

  const key = new DirectionalLight(0xffffff, 1.4);
  key.position.set(-2.5, 5, 3);
  scene.add(key);

  const glow = new PointLight(AMBER, 0, 12);
  glow.position.set(0, 2.2, 0);
  scene.add(glow);

  const rose = buildRose(disposables);
  scene.add(rose);

  const arrowGeometry = new ExtrudeGeometry(arrowShape(), {
    depth: 0.22,
    bevelEnabled: true,
    bevelThickness: 0.05,
    bevelSize: 0.045,
    bevelSegments: 3,
    curveSegments: 4,
  });
  // The shape is drawn in XY; lay it flat so its tip points along -Z, which is screen-up.
  arrowGeometry.rotateX(-Math.PI / 2);
  arrowGeometry.center();

  const arrowMaterial = new MeshStandardMaterial({
    color: AMBER,
    emissive: new Color(AMBER_DEEP),
    emissiveIntensity: 0.35,
    roughness: 0.32,
    metalness: 0.08,
  });
  disposables.push(arrowGeometry, arrowMaterial);

  const arrow = new Mesh(arrowGeometry, arrowMaterial);
  arrow.position.y = 0.42;
  scene.add(arrow);

  /* --- animation ------------------------------------------------------- */

  let target: ArrowSceneState = { relative: null, heading: null, onCourse: false, stale: false };
  let shownRelative = 0;
  let shownHeading = 0;
  let shownGlow = 0;
  let hasSnapped = false;

  let frame: number | null = null;
  let lastFrameAt = 0;

  const render = (now: number): void => {
    const delta = Math.min((now - lastFrameAt) / 1000, 0.25);
    lastFrameAt = now;

    if (target.relative !== null) {
      shownRelative = smoothAngle(shownRelative, target.relative, delta, ARROW.bearingSmoothing);
    }
    if (target.heading !== null) {
      shownHeading = smoothAngle(shownHeading, target.heading, delta, ARROW.headingSmoothing);
    }

    arrow.rotation.y = yawFor(shownRelative);
    // Counter-rotation: whichever way the phone turns, the rose turns the other way, so its
    // north mark keeps pointing at true north.
    rose.rotation.y = shownHeading * DEG2RAD;

    // Ease the glow rather than switching it, so "on course" arrives as a swell of light.
    const wantGlow = target.onCourse && !target.stale ? 1 : 0;
    shownGlow += (wantGlow - shownGlow) * Math.min(1, delta * 6);

    arrowMaterial.emissiveIntensity = 0.35 + shownGlow * 0.85;
    glow.intensity = shownGlow * 14;

    const visible = target.relative !== null;
    arrow.visible = visible;

    renderer.render(scene, camera);
    frame = window.requestAnimationFrame(render);
  };

  const start = (): void => {
    if (frame !== null) return;
    lastFrameAt = performance.now();
    frame = window.requestAnimationFrame(render);
  };

  const stop = (): void => {
    if (frame === null) return;
    window.cancelAnimationFrame(frame);
    frame = null;
  };

  // Battery: there is no point rendering a WebGL scene nobody is looking at.
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') stop();
    else start();
  };
  document.addEventListener('visibilitychange', onVisibility);

  /* --- sizing ----------------------------------------------------------- */

  const resize = (): void => {
    const width = canvas.clientWidth || 1;
    const height = canvas.clientHeight || 1;
    const aspect = width / height;

    renderer.setSize(width, height, false);
    camera.aspect = aspect;

    /*
     * Pull the camera back far enough that `FRAMED_RADIUS` fits on *both* axes.
     *
     * The vertical field of view is fixed, so on a portrait phone the horizontal one is much
     * narrower — fitting only the vertical extent is what made the first version of this scene
     * overflow the sides of the screen.
     */
    const halfFov = (FOV / 2) * DEG2RAD;
    const distance = FRAMED_RADIUS / (Math.tan(halfFov) * Math.min(1, aspect));

    camera.position.set(0, CAMERA_DIRECTION.y * distance, CAMERA_DIRECTION.z * distance);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  };

  const observer = new ResizeObserver(resize);
  observer.observe(canvas);

  start();

  return {
    canvas,
    update: (next) => {
      target = next;
      if (!hasSnapped && next.relative !== null) {
        hasSnapped = true;
        shownRelative = next.relative;
        shownHeading = next.heading ?? 0;
      }
    },
    snap: () => {
      shownRelative = target.relative ?? shownRelative;
      shownHeading = target.heading ?? shownHeading;
    },
    destroy: () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      observer.disconnect();
      for (const item of disposables) item.dispose();
      renderer.dispose();
      canvas.remove();
    },
  };
}

/** Exported for the unit tests: the angle the arrow should be shown at after `delta` seconds. */
export function nextShownAngle(current: number, target: number, delta: number): number {
  return normalizeDegrees(smoothAngle(current, target, delta, ARROW.bearingSmoothing));
}

/** Exported for the tests: a plain shortest-path step, used to prove the needle never wraps. */
export const stepTowards = (from: number, to: number, t: number): number => lerpAngle(from, to, t);
